import { Component, inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { LanguageService } from '@ose/services';
import { InputComponent } from '@ose/components';

import { Languages } from './languages';
import { LanguageModel } from './language.model';

@Component({
  selector: 'lib-language-switch',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, InputComponent],
  templateUrl: './language-switch.component.html',
  styleUrl: './language-switch.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LanguageSwitchComponent implements OnDestroy {
  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);

  readonly languages: LanguageModel[] = Languages.filter((language) =>
    this.languageService.languages.includes(language.shortName)
  );

  selectedLanguage = this.languageService.currentLang ?? 'en';

  private readonly subscriptions = new Subscription();

  constructor() {
    this.subscriptions.add(
      this.languageService.getCurrentLanguage().subscribe((currentLanguage) => {
        const languageExists = this.languages.some(
          (language) => language.shortName === currentLanguage
        );

        this.selectedLanguage = languageExists
          ? currentLanguage
          : this.languages[0]?.shortName ?? 'en';
      })
    );
  }

  get selectedLanguageName(): string {
    return this.getLanguageName(this.selectedLanguage);
  }

  getLanguageName(value: LanguageModel | string | null | undefined): string {
    const shortName = typeof value === 'string' ? value : value?.shortName;

    return (
      this.languages.find((language) => language.shortName === shortName)
        ?.name ??
      shortName?.toUpperCase() ??
      ''
    );
  }

  selectLanguage(value: LanguageModel | string | null | undefined): void {
    const shortName = typeof value === 'string' ? value : value?.shortName;

    if (!shortName) {
      return;
    }

    const languageExists = this.languages.some(
      (language) => language.shortName === shortName
    );

    if (!languageExists || this.selectedLanguage === shortName) {
      return;
    }

    this.selectedLanguage = shortName;

    this.languageService.setCurrentLanguage(shortName);
    this.updateLanguageInUrl(shortName);
  }

  private updateLanguageInUrl(language: string): void {
    const urlTree = this.router.parseUrl(this.router.url);

    if (!urlTree.queryParamMap.has('lang')) {
      return;
    }

    if (urlTree.queryParamMap.get('lang') === language) {
      return;
    }

    urlTree.queryParams = {
      ...urlTree.queryParams,
      lang: language,
    };

    void this.router.navigateByUrl(urlTree, {
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
